import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { Confirm, Modal, Select } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import Table from '@components/common/Table/Table';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm/FileTypeSelectionForm';

import { useMutateActiveAlarm } from '@hooks/alarms/alarmsByVendor/useMutateActiveAlarm';
import { useQueryActiveAlarmsList } from '@hooks/alarms/alarmsByVendor/useQueryActiveAlarmsList';
import { useQueryHistoryAlarmsList } from '@hooks/alarms/alarmsByVendor/useQueryHistoryAlarmsList';
import { useAlarmFilters } from '@hooks/alarms/alarmsByVendorFilters/useActiveAlarmFilters';
import { useQueryAlarmVendors } from '@hooks/alarms/useQueryAlarmVendors';
import { useWindowHeight } from '@hooks/utils/useWindowHeight';

import { getDateFilters } from '@utils/alarmUtils/commonDateAlarmFilter';
import { endOfDayInUTC, startOfDayInUTC } from '@utils/kpi-utils/date';
import { getAlarmFilterValues } from '@components/routes/AdminPanel/AlarmManagement/AlarmManagementByVendor/utils';

import { IActiveAlarmOran } from '@interfaces/alarms/alarm';
import { IExportDefaultDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IAlarmTableState } from './interfaces';

import { EAlarmStatus } from '@enums/alarms';
import {
	defaultMeta,
	EVendorsValues,
	TMenuActionsTypes,
	TVendorsValues,
} from '@enums/common';

import {
	exportRadioButtonsData,
	menuActionWithoutSelection,
} from '@constants/common';
import { MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import {
	ACTIVE_ALARMS_TABS_NAMES,
	ALARM_TABLE_EMPTY_TEXT,
	ALARM_TABS,
	DEFAULT_TABS,
} from './constants';
import { EXPORT_ALARMS_TOOLTIP_TEXT } from '@components/routes/AdminPanel/Alarms/constants';

import AcknowledgeAlarm from './AcknowledgeAlarm';
import AlarmDetails from './AlarmDetails';
import { getActiveAlarmsColumns } from './columns';
import { generateAlarmsFilters, getSearchTooltip } from './utils';

import styles from './AlarmByVendor.module.scss';

const OFFSET_HEIGHT = 270;

const AlarmByVendor = () => {
	const [alarms] = useSearchParams();

	const defaultTab = alarms.get('history') ? ACTIVE_ALARMS_TABS_NAMES.historyAlarms : ACTIVE_ALARMS_TABS_NAMES.activeAlarms;

	const [currentTab, setCurrentTab] = useState(defaultTab);

	const [tableState, setTableState] = useState<IAlarmTableState>(DEFAULT_TABS);
	const [activeExportAction, setActiveExportAction] = useState<TMenuActionsTypes | undefined>();
	const [selectedAlarmInformation, setSelectedAlarmInformation] = useState<IActiveAlarmOran | null>(null);
	const [selectedAlarmsIds, setSelectedAlarmsIds] = useState<string[] | null>(null);
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption<TVendorsValues> | null>(null);
	const [maxHeight, setMaxHeight] = useState('73vh');

	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const [isClearAlarmModal, setIsClearAlarmModal] = useState(false);
	const [isAcknowledgeAlarmModal, setIsAcknowledgeAlarmModal] = useState(false);

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		toDate,
		fromDate,
	} = tableState[currentTab];

	const { alarmVendorsOptions } = useQueryAlarmVendors();

	const windowHeight = useWindowHeight();
	const vendor = selectedVendor?.value;
	const isActiveTab = currentTab === ACTIVE_ALARMS_TABS_NAMES.activeAlarms;
	const isOranVendor = vendor === EVendorsValues.oran;
	const isFlexiDasVendor = vendor === EVendorsValues.flexidas;

	const startDate = useMemo(() => fromDate ? new Date(fromDate) : null, [fromDate]);
	const endDate = useMemo(() => toDate ? new Date(toDate) : null, [toDate]);
	const isSelectShown = alarmVendorsOptions.length > 1;

	const detailsId = isActiveTab && isFlexiDasVendor
		? selectedAlarmInformation?.alarmId
		: selectedAlarmInformation?.id;

	const { filtersList, isLoadingFilters } = useAlarmFilters({ vendor: vendor!, isActiveTab });

	const filterValues = useMemo(() => {
		return getAlarmFilterValues(tableState[currentTab].filter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableState[currentTab].filter]);

	const clearedUserId = tableState[currentTab].filter?.clearedType?.userId;

	const {
		activeAlarmList,
		countOfRecords,
		invalidateQuery: invalidateActiveAlarmsList,
		isLoadingTable: isLoadingActiveAlarmsTable,
	} = useQueryActiveAlarmsList({
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		vendor: vendor!,
		fromDate,
		toDate,
		isEnabled: isActiveTab,
		...filterValues,
		status: EAlarmStatus.active,
	});

	const {
		historyAlarmList,
		countOfRecords: countOfHistoryRecords,
		invalidateQuery: invalidateHistoryAlarmsList,
		isLoadingTable: isLoadingHistoryAlarmsTable,
	} = useQueryHistoryAlarmsList({
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		vendor: vendor!,
		fromDate,
		toDate,
		isEnabled: !isActiveTab,
		...clearedUserId ? { clearedUserId } : undefined,
		...filterValues,
	});

	const toggleFileTypeSelectionModal = useCallback(() => {
		setIsOpenFileTypeSelectionModal(prevState => !prevState);
	}, []);

	const closeTypeSelectionForm = useCallback(() => setIsOpenFileTypeSelectionModal(false), []);

	const openDetailsModal = useCallback(() => {
		setIsDetailsOpen(true);
	}, []);

	const closeDetailsModal = useCallback(() => {
		setIsDetailsOpen(false);
		setSelectedAlarmInformation(null);
	}, []);

	const openClearAlarmModal = useCallback(() => setIsClearAlarmModal(true), []);
	const closeClearAlarmModal = useCallback(() => {
		setIsClearAlarmModal(false);
		invalidateActiveAlarmsList();
		invalidateHistoryAlarmsList();
	}, [invalidateActiveAlarmsList, invalidateHistoryAlarmsList]);

	const openAcknowledgeAlarmModal = useCallback(() => setIsAcknowledgeAlarmModal(true), []);
	const closeAcknowledgeAlarmModal = useCallback(() => {
		setIsAcknowledgeAlarmModal(false);
		invalidateActiveAlarmsList();
		invalidateHistoryAlarmsList();
	}, [invalidateActiveAlarmsList, invalidateHistoryAlarmsList]);

	const {
		acknowledgeAlarm,
		clearAlarm,
		handlePreparingExport,
		isLoadingAlarm,
	} = useMutateActiveAlarm({
		onSuccessClear: closeClearAlarmModal,
		onSuccessAcknowledge: closeAcknowledgeAlarmModal,
		vendor: vendor!,
		isActiveTab,
	});

	const onVendorChange = (option: TSelectOption<TVendorsValues>) => {
		setSelectedVendor(option);
	};

	const handleClearAlarm = useCallback(() => {
		if (!selectedAlarmInformation?.id) return;

		clearAlarm({ id: selectedAlarmInformation?.id });
	}, [clearAlarm, selectedAlarmInformation]);

	const isLoadingTable = isLoadingActiveAlarmsTable
		|| isLoadingHistoryAlarmsTable
		|| isLoadingFilters;

	const activeExport = useCallback((data: IExportDefaultDTO) => {
		handlePreparingExport(data);
	}, [handlePreparingExport]);

	const handleAcknowledgeAlarm = useCallback((id: string, acknowledgeNotes: string) => {
		if (!id) return;

		acknowledgeAlarm({ id, acknowledgeNotes });
	}, [acknowledgeAlarm]);

	const handleExport = useCallback((alarmsIds: Array<string>, actionId: TMenuActionsTypes) => {
		setSelectedAlarmsIds(alarmsIds);
		setActiveExportAction(actionId);

		toggleFileTypeSelectionModal();
	}, [toggleFileTypeSelectionModal]);

	const columns = useMemo(() => {
		return getActiveAlarmsColumns({
			openDetailsModal,
			setSelectedAlarmInformation,
			openClearAlarmModal,
			openAcknowledgeAlarmModal,
			isActiveTab,
			vendor: vendor!,
		});
	}, [
		openDetailsModal,
		setSelectedAlarmInformation,
		openClearAlarmModal,
		openAcknowledgeAlarmModal,
		vendor,
		isActiveTab,
	]);

	const clearPagination = useCallback(() => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				page: defaultMeta.page,
				pageSize: defaultMeta.pageSize,
			},
		}));
	}, [setTableState, currentTab]);

	const clearTableState = useCallback(() => {
		setTableState(DEFAULT_TABS);
	}, [setTableState]);

	useEffect(() => {
		clearTableState();
		setSelectedAlarmInformation(null);
	}, [vendor, clearTableState]);

	const onChangeFromDate = useCallback((value: number | null) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				fromDate: value,
			},
		}));
	}, [setTableState, currentTab]);

	const onChangeToDate = useCallback((value: number | null) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				toDate: value,
			},
		}));
	}, [setTableState, currentTab]);

	const dateFilters: Array<ITableDatepickerFilter> = useMemo(() => {
		return getDateFilters({
			onChangeFromDate,
			onChangeToDate,
			startDate,
			endDate,
		});
	}, [
		onChangeFromDate,
		onChangeToDate,
		startDate,
		endDate,
	]);

	const setFilters = useCallback((value: Record<string, TSelectOption>) => {
		clearPagination();
		setTableState((prevState) => {
				return {
					...prevState,
					[currentTab]: {
						...prevState[currentTab],
						filter: {
							...prevState[currentTab].filter,
							...value,
						},
					},
				};
			},
		);
	}, [currentTab, setTableState, clearPagination]);

	const filters: TTableFilter[] = useMemo(() => {
		return generateAlarmsFilters(
			filtersList,
			tableState[currentTab].filter,
			setFilters,
		);
	}, [filtersList, currentTab, tableState, setFilters]);

	const searchHandler = useCallback((searchValue: string) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				searchValue,
				page: defaultMeta.page,
			},
		}));

	}, [setTableState, currentTab]);

	const pageHandler = useCallback((page: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				page,
			},
		}));
	}, [currentTab]);

	const pageSizeHandler = useCallback((pageSize: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				pageSize,
				page: defaultMeta.page,
			},
		}));
	}, [currentTab]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				sortDirection: sortBy[0]?.desc || false,
				sortFieldName: sortBy[0]?.id,
			},
		}));
	}, [currentTab]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: isActiveTab ? countOfRecords : countOfHistoryRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		isActiveTab,
		countOfHistoryRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: getSearchTooltip(vendor!, isActiveTab),
	}], [searchValue, searchHandler, vendor, isActiveTab]);

	const isOpenFilters = useMemo(() => {
		const hasActiveFilters = !!Object.values(tableState[currentTab].filter).filter(Boolean).length;

		return tableState[currentTab].filter && hasActiveFilters || null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableState[currentTab].filter, currentTab]);

	useEffect(() => {
		const maxHeight = windowHeight - OFFSET_HEIGHT;

		setMaxHeight(`${maxHeight}px`);
	}, [windowHeight]);

	useEffect(() => {
		if (!selectedVendor && alarmVendorsOptions.length) {
			setSelectedVendor(alarmVendorsOptions[0]);
		}
	}, [selectedVendor, alarmVendorsOptions]);

	const getCurrentTab = useCallback(() => {
		switch (currentTab) {
			case ACTIVE_ALARMS_TABS_NAMES.activeAlarms:
				return (
					<Table
						key={ACTIVE_ALARMS_TABS_NAMES.activeAlarms}
						columns={columns}
						customExportActions={menuActionWithoutSelection}
						data={activeAlarmList}
						dateFilters={!isOranVendor ? dateFilters : undefined}
						emptyText={ALARM_TABLE_EMPTY_TEXT}
						exportTooltipText={EXPORT_ALARMS_TOOLTIP_TEXT}
						filters={filters}
						handleExport={handleExport}
						handleRefresh={invalidateActiveAlarmsList}
						isExport
						isLoading={isLoadingTable}
						isOpenFilters={isOpenFilters}
						isOpenSearch={!!searchValue}
						isPinColumns
						isRefreshTable
						isWithoutCheckbox
						isWithoutDeleteAction
						maxHeight={maxHeight}
						pagination={pagination}
						search={search}
						searchThreshold={0}
						sort={sort}
						stickyLeftColumnIndexes={[0, 1]}
					/>
				);
			case ACTIVE_ALARMS_TABS_NAMES.historyAlarms:
				return (
					<Table
						key={ACTIVE_ALARMS_TABS_NAMES.historyAlarms}
						columns={columns}
						customExportActions={menuActionWithoutSelection}
						data={historyAlarmList}
						dateFilters={!isOranVendor ? dateFilters : undefined}
						emptyText={ALARM_TABLE_EMPTY_TEXT}
						exportTooltipText={EXPORT_ALARMS_TOOLTIP_TEXT}
						filters={filters}
						handleExport={handleExport}
						handleRefresh={invalidateHistoryAlarmsList}
						isExport
						isLoading={isLoadingTable}
						isOpenFilters={isOpenFilters}
						isOpenSearch={!!searchValue}
						isPinColumns
						isRefreshTable
						isWithoutCheckbox
						isWithoutDeleteAction
						maxHeight={maxHeight}
						pagination={pagination}
						search={search}
						searchThreshold={0}
						sort={sort}
						stickyLeftColumnIndexes={[0, 1]}
					/>
				);
			default:
				return null;
		}
	}, [
		currentTab,
		columns,
		activeAlarmList,
		isOranVendor,
		dateFilters,
		filters,
		handleExport,
		invalidateActiveAlarmsList,
		isLoadingTable,
		isOpenFilters,
		searchValue,
		maxHeight,
		pagination,
		search,
		sort,
		historyAlarmList,
		invalidateHistoryAlarmsList,
	]);

	return (
		<div className={styles.activeAlarmsContainer}>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_480}
			>
				<FileTypeSelectionForm
					activeGroupTableAction={activeExportAction}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					filter={{
						...filterValues,
						page,
						pageSize,
						sortDirection,
						sortFieldName,
						searchValue,
						from: fromDate && startOfDayInUTC(new Date(fromDate)),
						to: toDate && endOfDayInUTC(new Date(toDate)),
					}}
					handlePreparingExport={activeExport}
					isLoading={isLoadingAlarm}
					selectedIds={selectedAlarmsIds}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			<Modal
				className={styles.higherModalIndex}
				isOpen={isClearAlarmModal}
				onClose={closeClearAlarmModal}
			>
				<Confirm
					isLoading={isLoadingAlarm}
					onClose={closeClearAlarmModal}
					onSubmit={handleClearAlarm}
					question='Are you sure you want to clear the alarm?'
				/>
			</Modal>
			{vendor && selectedAlarmInformation && (
				<Modal
					className={styles.higherModalIndex}
					isOpen={isAcknowledgeAlarmModal}
					isScrollableContent
					onClose={closeAcknowledgeAlarmModal}
					title={isDetailsOpen && selectedAlarmInformation?.isAcknowledged ? 'Edit acknowledge notes' : 'Mark as acknowledged'}
					width={MODAL_WIDTH_580}
				>
					<AcknowledgeAlarm
						id={selectedAlarmInformation?.id}
						isLoading={isLoadingAlarm}
						onClose={closeAcknowledgeAlarmModal}
						onSubmit={handleAcknowledgeAlarm}
						setSelectedAlarm={setSelectedAlarmInformation}
						vendor={vendor}
					/>
				</Modal>
			)}
			{detailsId && vendor && (
				<Modal
					isOpen={isDetailsOpen}
					isScrollableContent
					onClose={closeDetailsModal}
					title='Details'
					width={MODAL_WIDTH_580}
				>
					<AlarmDetails
						id={detailsId as string}
						isHistory={!isActiveTab}
						onAcknowledge={openAcknowledgeAlarmModal}
						onClear={openClearAlarmModal}
						onClose={closeDetailsModal}
						vendor={vendor}
					/>
				</Modal>
			)}
			{isSelectShown && (
				<div className={styles.selectWrapper}>
					<Select
						disabled={!isSelectShown}
						handleChange={onVendorChange}
						isWithoutUseForms
						label='Vendor'
						name='vendor'
						options={alarmVendorsOptions}
						placeholder='Select vendor'
						value={selectedVendor}
						width='240px'
					/>
				</div>
			)}
			<div className={styles.activeAlarmsTableContainer}>
				<ProfileTabSection
					currentTab={currentTab}
					getCurrentTab={getCurrentTab}
					isFullHeight
					isWithoutShadow
					maxWidth={'unset'}
					setCurrentTab={setCurrentTab}
					tabs={ALARM_TABS}
				/>
			</div>
		</div>
	);
};

export default memo(AlarmByVendor);
